import { AsyncData } from '@model/common';
import { DataEnvelope } from '@model/iceberg';
import { ExtrasOptions } from '@model/iceberg/booking/extras/extras-options';
import { BookingApi } from '@model/iceberg/service/booking';
import { fetchBasket } from '@state/basket';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

export enum ExtrasOptionsActions {
  NAME = '@EXTRAS_OPTIONS',
  FETCH_EXTRAS_OPTIONS = '@EXTRAS_OPTIONS/FETCH_EXTRAS_OPTIONS',
  CLEAR_EXTRAS_OPTIONS = '@EXTRAS_OPTIONS/CLEAR_EXTRAS_OPTIONS',
  SET_EXTRA_OPTION = '@EXTRAS_OPTIONS/SET_EXTRA_OPTION',
  RECEIVE_EXTRAS_OPTIONS_SUCCESS = '@EXTRAS_OPTIONS/RECEIVE_EXTRAS_OPTIONS_SUCCESS',
  RECEIVE_EXTRAS_OPTIONS_FAILURE = '@EXTRAS_OPTIONS/RECEIVE_EXTRAS_OPTIONS_FAILURE'
}

export interface SetExtraOptionParams {
  searchToken: string;
  token: string;
  optionToken: string;
}

export const INITIAL_EXTRAS_OPTIONS_STATE: AsyncData<Array<ExtrasOptions>> = {
  data: [],
  loading: false,
  error: null
};

export const fetchExtrasOptions = createAsyncThunk(
  ExtrasOptionsActions.FETCH_EXTRAS_OPTIONS,
  async (token: string, { rejectWithValue }) => {
    const bookingApi: BookingApi = new BookingApi();

    try {
      const response: DataEnvelope<Array<ExtrasOptions>> = await bookingApi.getExtrasOptions(token);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const setExtraOption = createAsyncThunk(
  ExtrasOptionsActions.SET_EXTRA_OPTION,
  async (payload: SetExtraOptionParams, { dispatch, rejectWithValue }) => {
    const { searchToken, token, optionToken } = payload;

    const bookingApi: BookingApi = new BookingApi();
    try {
      await bookingApi.putExtrasOptions({
        searchToken,
        token,
        optionToken
      });
      dispatch(fetchBasket(searchToken));
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const extrasOptionsSlice = createSlice({
  name: ExtrasOptionsActions.NAME,
  initialState: INITIAL_EXTRAS_OPTIONS_STATE,
  reducers: {
    clearExtrasOptions: () => INITIAL_EXTRAS_OPTIONS_STATE
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExtrasOptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchExtrasOptions.fulfilled, (state: any = INITIAL_EXTRAS_OPTIONS_STATE, action) => {
        state.data = action.payload.data || [];
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchExtrasOptions.rejected, (state, action) => {
        state.data = INITIAL_EXTRAS_OPTIONS_STATE.data;
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export const { clearExtrasOptions } = extrasOptionsSlice.actions;
export const extrasOptionsReducer = extrasOptionsSlice.reducer;
